// client/src/components/AvailabilityList.js
import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import ApiAvailability from '../../api/ApiAvailability'
import PageLayout from '../common/layout/PageLayout'
import toastr from 'toastr'
import { format } from 'date-fns'
import { formatDateTime } from '../../utils/format'
import { parseRRule, describeRecurrence } from '../../utils/recurrence'
import services from '../../services'
import DeleteRecurringDialog from './DeleteRecurringDialog'
import './AvailabilityList.css'

function AvailabilityList() {
  const [availabilities, setAvailabilities] = useState([])
  const [loading, setLoading] = useState(true)
  const [divisionInfo, setDivisionInfo] = useState(null)
  const [deleteDialog, setDeleteDialog] = useState(null) // { availability, instanceDate }
  const navigate = useNavigate()
  const { leagueId } = useParams()

  const loadDivisionInfo = useCallback(async () => {
    try {
      const divResponse = await services.league.getMyDivision(leagueId)
      if (divResponse.success) {
        setDivisionInfo(divResponse.data)
      }
    } catch (err) {
      console.error('Failed to load division info:', err)
    }
  }, [leagueId])

  const fetchAvailabilities = useCallback(async () => {
    try {
      setLoading(true)
      let response

      if (leagueId && divisionInfo) {
        // League-specific availability
        response = await ApiAvailability.getMyAvailability(
          leagueId,
          divisionInfo.division_id,
        )
      } else {
        // Global availability (fallback)
        response = await ApiAvailability.getMyAvailability()
      }

      if (response.success) {
        setAvailabilities(response.data?.availabilities || [])
      } else {
        toastr.error('Failed to load availabilities')
      }
    } catch (err) {
      console.error('Failed to load availabilities:', err)
      toastr.error('Failed to load availabilities')
    } finally {
      setLoading(false)
    }
  }, [leagueId, divisionInfo])

  useEffect(() => {
    if (leagueId) {
      loadDivisionInfo()
    }
  }, [leagueId, loadDivisionInfo])

  useEffect(() => {
    if (!leagueId || divisionInfo) {
      fetchAvailabilities()
    }
  }, [leagueId, divisionInfo, fetchAvailabilities])

  const handleDelete = async (availability, instanceDate = null) => {
    const isSharedSlot = Boolean(availability.shared_team_slot_id)
    // For recurring availability, show dialog to choose delete type
    if (availability.is_recurring && !instanceDate) {
      setDeleteDialog({ availability })
      return
    }

    // For non-recurring, confirm deletion
    if (
      !availability.is_recurring &&
      !window.confirm(
        isSharedSlot
          ? 'This removes the slot for both you and your partner.'
          : 'This removes your personal slot.',
      )
    ) {
      return
    }

    try {
      let response

      if (instanceDate) {
        // Delete single instance by adding exception date
        response = isSharedSlot
          ? await ApiAvailability.addTeamException(
              availability.shared_team_slot_id,
              instanceDate,
            )
          : await ApiAvailability.addException(availability.id, instanceDate)
        if (response.success) {
          toastr.success(
            'This instance has been removed from the recurring schedule',
          )
        }
      } else {
        // Delete entire availability
        response = isSharedSlot
          ? await ApiAvailability.deleteTeamAvailability(
              availability.shared_team_slot_id,
            )
          : await ApiAvailability.deleteAvailability(availability.id)
        if (response.success) {
          toastr.success('Availability deleted successfully')
        }
      }

      if (response.success) {
        await fetchAvailabilities()
      } else {
        toastr.error(response.error || 'Failed to delete availability')
      }
    } catch (err) {
      console.error('Failed to delete availability:', err)
      toastr.error('Failed to delete availability')
    }
  }

  const handleDeleteThisInstance = async () => {
    const { availability } = deleteDialog
    setDeleteDialog(null)

    // Use the original start_time as the exception date
    await handleDelete(availability, availability.start_time)
  }

  const handleDeleteAllInstances = async () => {
    const { availability } = deleteDialog
    setDeleteDialog(null)
    await handleDelete(availability, null)
  }

  const handleCancelDelete = () => {
    setDeleteDialog(null)
  }

  const formatTimeRange = (startTime, endTime) => {
    try {
      const start = formatDateTime(startTime)
      const end = format(new Date(endTime), 'h:mm a')
      return `${start} - ${end}`
    } catch {
      // Format error, return raw values
      return `${startTime} - ${endTime}`
    }
  }

  const formatRecurrenceDescription = (availability) => {
    if (!availability.is_recurring || !availability.recurrence_rule) {
      return null
    }

    try {
      // Parse the RRULE to get days, interval, and endDate
      const { days, interval, endDate } = parseRRule(
        availability.recurrence_rule,
      )

      // Generate human-readable description
      return describeRecurrence(days, interval, endDate)
    } catch (error) {
      console.error('Error formatting recurrence description:', error)
      // Fallback to raw rule if parsing fails
      return availability.recurrence_rule
    }
  }

  const formatEditorName = (availability) => {
    if (availability.edited_by_name) return availability.edited_by_name
    const name = [
      availability.edited_by_firstname,
      availability.edited_by_lastname,
    ]
      .filter(Boolean)
      .join(' ')
    if (name) return name
    return availability.edited_by_user_id
      ? `user #${availability.edited_by_user_id}`
      : null
  }

  const isDoublesLeague = divisionInfo?.league_type === 'Doubles'

  if (loading) {
    return (
      <PageLayout title="My Availability">
        <div className="content-card">
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            Loading availability...
          </div>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout title="My Availability">
      <div className="content-card">
        <div className="availability-header">
          <button
            className="btn btn-primary"
            onClick={() =>
              navigate(
                leagueId
                  ? `/leagues/${leagueId}/availability/add`
                  : '/availability/add',
              )
            }
          >
            Add Availability
          </button>
          {leagueId && (
            <button
              className="btn btn-secondary"
              onClick={() => navigate(`/leagues/${leagueId}/find-matches`)}
            >
              View Schedule Overlaps
            </button>
          )}
        </div>

        {availabilities.length > 0 ? (
          <div className="availability-list">
            {availabilities.map((availability) => (
              <div key={availability.id} className="availability-item">
                <div className="availability-info">
                  <div className="availability-time">
                    {formatTimeRange(
                      availability.start_time,
                      availability.end_time,
                    )}
                  </div>
                  {isDoublesLeague && (
                    <div className="availability-scope-row">
                      <span
                        className={`availability-scope-badge ${
                          availability.shared_team_slot_id ? 'team' : 'personal'
                        }`}
                      >
                        {availability.shared_team_slot_id ? 'Team' : 'Just you'}
                      </span>
                      {availability.shared_team_slot_id &&
                        formatEditorName(availability) && (
                          <span className="availability-editor">
                            Last edited by {formatEditorName(availability)}
                          </span>
                        )}
                    </div>
                  )}
                  {availability.is_recurring && (
                    <div className="availability-recurring">
                      <span className="recurring-badge">Recurring</span>
                      {availability.recurrence_rule && (
                        <span className="recurrence-description">
                          {formatRecurrenceDescription(availability)}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="availability-created">
                    Created: {formatDateTime(availability.created_at)}
                  </div>
                </div>
                <div className="availability-actions">
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDelete(availability)}
                    title="Delete availability"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-availability-message">
            <h3>No availability scheduled</h3>
            <p>
              You haven't set any availability yet. Add your available times to
              start receiving match proposals.
            </p>
            <button
              className="btn btn-primary"
              onClick={() =>
                navigate(
                  leagueId
                    ? `/leagues/${leagueId}/availability/add`
                    : '/availability/add',
                )
              }
              style={{ marginTop: '1rem' }}
            >
              Add Your First Availability
            </button>
          </div>
        )}
      </div>

      {deleteDialog && (
        <DeleteRecurringDialog
          onDeleteThis={handleDeleteThisInstance}
          onDeleteAll={handleDeleteAllInstances}
          onCancel={handleCancelDelete}
        />
      )}
    </PageLayout>
  )
}

export default AvailabilityList
